import React from 'react';
import { Link } from 'react-router-dom';
import MarketingLayout from '../marketing/MarketingLayout';
import { CityData } from '../../data/locations';
import { ArrowRightIcon, BuildingOfficeIcon, ChartBarIcon, CurrencyDollarIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import OptimizedImage from '../common/OptimizedImage';

interface LocationPageTemplateProps {
  city: CityData;
}

export default function LocationPageTemplate({ city }: LocationPageTemplateProps) {
  // Calculate tax savings example
  const annualIncome = 250000;
  const corporateTaxSavings = Math.round((annualIncome * 0.5) * (city.taxRates.provincial + city.taxRates.federal - 0.135));

  return (
    <MarketingLayout>
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="absolute inset-0 opacity-20">
          <img
            src={`/assets/cities/${city.slug}-hero.svg`}
            alt={`${city.name} skyline`}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 container mx-auto px-4 py-20">
          <div className="max-w-4xl">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm mb-6">
              <BuildingOfficeIcon className="h-4 w-4 mr-2" />
              {city.province} Financial Services
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Expert Financial Planning & Wealth Management in {city.name}
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              {city.description}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact" className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg font-medium hover:from-blue-600 hover:to-blue-700 transition-all">
                Book Your Consultation
                <ArrowRightIcon className="h-5 w-5 ml-2" />
              </Link>
              <button className="inline-flex items-center px-6 py-3 bg-white/10 backdrop-blur text-white rounded-lg font-medium hover:bg-white/20 transition-all">
                Download {city.name} Tax Guide
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Local Market Insights */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {city.name} Market Insights & Opportunities
            </h2>
            <p className="text-lg text-gray-600 mb-12">
              Understanding the local economic landscape is crucial for effective financial planning.
            </p>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="bg-gray-50 rounded-xl p-6">
                <ChartBarIcon className="h-10 w-10 text-blue-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Economic Overview</h3>
                <p className="text-gray-600 mb-4">{city.marketInsights.economicGrowth}</p>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Average Household Income</span>
                    <span className="font-semibold text-gray-900">${city.avgHouseholdIncome.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Population</span>
                    <span className="font-semibold text-gray-900">{city.population.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Provincial Tax Rate</span>
                    <span className="font-semibold text-gray-900">{(city.taxRates.provincial * 100).toFixed(1)}%</span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <UserGroupIcon className="h-10 w-10 text-green-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Major Industries</h3>
                <p className="text-gray-600 mb-4">{city.marketInsights.demographics}</p>
                <div className="flex flex-wrap gap-2">
                  {city.majorIndustries.map((industry, index) => (
                    <span key={index} className="px-3 py-1 bg-white rounded-full text-sm text-gray-700 border border-gray-200">
                      {industry}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-8">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Investment Opportunities in {city.name}</h3>
              <p className="text-gray-700 mb-6">{city.marketInsights.investmentOpportunities}</p>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Key Employers</h4>
                  <ul className="space-y-1">
                    {city.marketInsights.keyEmployers.slice(0, 3).map((employer, index) => (
                      <li key={index} className="text-sm text-gray-600">• {employer}</li>
                    ))}
                  </ul>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Tax Advantages</h4>
                  <p className="text-sm text-gray-600">
                    Save up to ${corporateTaxSavings.toLocaleString()} annually through proper corporate structuring
                  </p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Growth Sectors</h4>
                  <p className="text-sm text-gray-600">
                    {city.majorIndustries.slice(0, 2).join(' & ')} leading regional growth
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Comprehensive Financial Services for {city.name} Residents
            </h2>
            <p className="text-lg text-gray-600 mb-12">
              Tailored strategies that address the unique financial landscape of {city.province}.
            </p>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <CurrencyDollarIcon className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Investment Management</h3>
                <p className="text-gray-600 mb-4">
                  Build wealth with strategies optimized for {city.province}'s tax environment and local opportunities.
                </p>
                <Link to="/services" className="text-blue-600 font-medium hover:text-blue-700 inline-flex items-center">
                  Learn More <ArrowRightIcon className="h-4 w-4 ml-1" />
                </Link>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                  <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Insurance Planning</h3>
                <p className="text-gray-600 mb-4">
                  Protect your family and business with comprehensive coverage designed for {city.name} professionals.
                </p>
                <Link to="/services" className="text-blue-600 font-medium hover:text-blue-700 inline-flex items-center">
                  Learn More <ArrowRightIcon className="h-4 w-4 ml-1" />
                </Link>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                  <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Estate Planning</h3>
                <p className="text-gray-600 mb-4">
                  Navigate {city.province}'s estate laws to preserve wealth and create lasting legacies.
                </p>
                <Link to="/services" className="text-blue-600 font-medium hover:text-blue-700 inline-flex items-center">
                  Learn More <ArrowRightIcon className="h-4 w-4 ml-1" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Calculator CTA */}
      <section className="py-16 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Calculate Your Tax Savings in {city.name}
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Discover how much you could save with proper financial planning tailored to {city.province}'s tax environment.
            </p>
            <div className="bg-white/5 backdrop-blur rounded-xl p-8 max-w-2xl mx-auto border border-white/10">
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-2">Annual Income</label>
                  <input 
                    type="text" 
                    className="w-full px-4 py-2 rounded-lg bg-white/10 text-white placeholder-gray-400 border border-white/20 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors"
                    placeholder="$250,000"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-2">Business Type</label>
                  <select className="w-full px-4 py-2 rounded-lg bg-white/10 text-white border border-white/20 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors">
                    <option>Professional Corporation</option>
                    <option>Small Business</option>
                    <option>Sole Proprietor</option>
                  </select>
                </div>
              </div>
              <button className="w-full md:w-auto px-8 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg font-semibold hover:from-amber-600 hover:to-amber-700 transition-all transform hover:scale-105">
                Calculate My Savings
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Local Resources */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              {city.name} Financial Planning Resources
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Link to="#" className="group">
                <div className="bg-gray-50 rounded-lg p-6 text-center hover:bg-gray-100 transition-colors">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-200 transition-colors">
                    <svg className="h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{city.province} Tax Guide</h3>
                  <p className="text-sm text-gray-600">2024 Edition</p>
                </div>
              </Link>

              <Link to="#" className="group">
                <div className="bg-gray-50 rounded-lg p-6 text-center hover:bg-gray-100 transition-colors">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-green-200 transition-colors">
                    <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">ROI Calculator</h3>
                  <p className="text-sm text-gray-600">Investment Tools</p>
                </div>
              </Link>

              <Link to="#" className="group">
                <div className="bg-gray-50 rounded-lg p-6 text-center hover:bg-gray-100 transition-colors">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-purple-200 transition-colors">
                    <svg className="h-8 w-8 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Estate Guide</h3>
                  <p className="text-sm text-gray-600">{city.province} Laws</p>
                </div>
              </Link>

              <Link to="#" className="group">
                <div className="bg-gray-50 rounded-lg p-6 text-center hover:bg-gray-100 transition-colors">
                  <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-amber-200 transition-colors">
                    <svg className="h-8 w-8 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Business Guide</h3>
                  <p className="text-sm text-gray-600">Local Resources</p>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions About Financial Planning in {city.name}
            </h2>
            
            <div className="space-y-6">
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  What are the tax advantages of incorporating in {city.province}?
                </h3>
                <p className="text-gray-600">
                  {city.province} offers a combined federal and provincial small business tax rate that can save you thousands annually. 
                  Professional corporations can benefit from income splitting opportunities and tax deferral strategies specific to provincial regulations.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  How does {city.name}'s real estate market affect financial planning?
                </h3>
                <p className="text-gray-600">
                  With {city.name}'s dynamic real estate market, proper financial planning can help you navigate property investments, 
                  mortgage strategies, and leverage home equity for wealth building while managing associated risks.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  What retirement savings strategies work best for {city.name} residents?
                </h3>
                <p className="text-gray-600">
                  Beyond traditional RRSPs and TFSAs, {city.name} residents can benefit from strategies tailored to local income levels 
                  and cost of living, including pension optimization, tax-efficient withdrawal planning, and estate preservation techniques.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  How can I protect my business in {city.name}'s economic environment?
                </h3>
                <p className="text-gray-600">
                  Our strategies include key person insurance, buy-sell agreements, and business succession planning specifically 
                  designed for {city.name}'s business landscape, protecting against local market volatility and ensuring continuity.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-light mb-8">
              Ready to Optimize Your Financial Future in {city.name}?
            </h2>
            <p className="text-xl mb-12 text-gray-300 font-light max-w-3xl mx-auto leading-relaxed">
              Join thousands of {city.name} families and businesses who trust PCM Financial for comprehensive wealth management.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link 
                to="/contact" 
                className="inline-block bg-amber-500 hover:bg-amber-600 text-white font-medium py-5 px-14 rounded-md shadow-lg hover:shadow-xl transition-all duration-300 tracking-wide transform hover:-translate-y-0.5"
              >
                Schedule Your Free Consultation
              </Link>
              <a 
                href="tel:306-861-4995"
                className="inline-block bg-transparent border-2 border-white hover:bg-white hover:text-slate-900 text-white font-medium py-5 px-14 rounded-md transition-all duration-300 tracking-wide"
              >
                Call 306-861-4995
              </a>
            </div>
            <p className="mt-10 text-gray-400 text-sm">
              Serving {city.name} and surrounding areas with offices across {city.province}
            </p>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}